/**
 * Texterkennung auf dem Gerät (Elternbrief-Leser): tesseract.js mit deutschem
 * Modell, alles aus der eigenen App (`ocrAssets.ts`). Nichts wird
 * hochgeladen — das Bild geht nur in den Web Worker dieses Browsers.
 *
 * tesseract.js selbst wird erst hier, beim ersten Lesen, nachgeladen — es
 * steckt nicht im Haupt-Bundle.
 */

import { OCR_BASE } from './ocrAssets.js';

export type OcrStage = 'loading' | 'reading';

export interface OcrProgress {
  stage: OcrStage;
  /** 0 … 1 */
  progress: number;
}

/** Liest Text aus einem Bild. Abbrechbar über `signal`. */
export type Recognize = (
  image: Blob,
  onProgress: (progress: OcrProgress) => void,
  signal: AbortSignal,
) => Promise<string>;

export class OcrAbortedError extends Error {
  constructor() {
    super('Texterkennung abgebrochen');
  }
}

/** Lange Seite fürs Lesen — genug für Briefschrift, klein genug für ein iPhone. */
const MAX_SIDE = 2400;

/**
 * Bild drehen (EXIF) und verkleinern. Große Kamerafotos (12 MP und mehr)
 * sprengen sonst den Speicher von iOS Safari im Worker.
 */
async function prepare(image: Blob): Promise<Blob | HTMLCanvasElement> {
  if (typeof createImageBitmap !== 'function') return image;
  const bitmap = await createImageBitmap(image, { imageOrientation: 'from-image' });
  try {
    const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const context = canvas.getContext('2d');
    if (!context) return image;
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return canvas;
  } finally {
    bitmap.close();
  }
}

export const recognizeOnDevice: Recognize = async (image, onProgress, signal) => {
  onProgress({ stage: 'loading', progress: 0 });
  const { createWorker, OEM } = await import('tesseract.js');
  if (signal.aborted) throw new OcrAbortedError();
  const source = await prepare(image);
  const worker = await createWorker('deu', OEM.LSTM_ONLY, {
    workerPath: `${OCR_BASE}/worker.min.js`,
    corePath: OCR_BASE,
    langPath: OCR_BASE,
    // Eigener Ursprung statt Blob-Worker: passt zur engen Content-Security-Policy.
    workerBlobURL: false,
    // Den Cache hält der Service Worker (`OCR_CACHE`), nicht IndexedDB doppelt.
    cacheMethod: 'none',
    logger: (message: { status: string; progress: number }) => {
      if (signal.aborted) return;
      onProgress({
        stage: message.status === 'recognizing text' ? 'reading' : 'loading',
        progress: Number.isFinite(message.progress) ? message.progress : 0,
      });
    },
  });
  const stop = () => void worker.terminate();
  signal.addEventListener('abort', stop, { once: true });
  try {
    if (signal.aborted) throw new OcrAbortedError();
    const result = await worker.recognize(source);
    if (signal.aborted) throw new OcrAbortedError();
    return result.data.text;
  } catch (error) {
    if (signal.aborted) throw new OcrAbortedError();
    throw error;
  } finally {
    signal.removeEventListener('abort', stop);
    void worker.terminate();
  }
};
